import { ConnectionsPage } from '@/features/connections/Connections'
import { createRootRouteWithContext, createRoute, createRouter, Outlet, redirect, useRouter, type ErrorComponentProps } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { lazy, Suspense } from 'react'
import type { Me } from '@one-deployment/api'
import { Button, SkeletonRows } from '@/components/ui'
import { EmptyState, ErrorState } from '@/components/status'
import { fetchMe } from '@/features/auth/useMe'
import { LoginPage } from '@/features/auth/Login'
import { TwoFactorPage } from '@/features/auth/TwoFactor'
import { EnrolPage } from '@/features/auth/Enrol'
import { ForgotPage, InvitePage, ResetPage } from '@/features/auth/Links'
import { Shell } from '@/features/shell/Shell'
import { HomePage } from '@/features/home/Home'
import { ProjectOverviewPage } from '@/features/projects/Overview'
import { IssuesPage } from '@/features/linear/Issues'
import { IntakePage } from '@/features/linear/Intake'
import { DetailedScopePage } from '@/features/projects/DetailedScope'
import { ScopesPage } from '@/features/scopes/Scopes'
import { ArchivePage } from '@/features/projects/Archive'
import { ServerPage, ServersPage } from '@/features/servers/Servers'
import { UsagePage } from '@/features/usage/Usage'
import { AlertsPage } from '@/features/alerts/Alerts'
import { PeoplePage } from '@/features/people/People'
import { AccountPage } from '@/features/account/Account'
import { ClaudeAccountsPage } from '@/features/claude/ClaudeAccounts'
import { CiRunPage, CiRunsPage } from '@/features/ci/Ci'
import { withSettingsTabs } from '@/features/shell/SettingsTabs'
import { BackupsPage } from '@/features/backups/Backups'
import { can, challengeFor } from '@/lib/can'
import type { Capability } from '@one-deployment/shared'
import { qk } from '@/lib/query'

type Ctx = { qc: QueryClient }

// xterm and the diff renderer (with its grammars) are most of the bundle; only the workspace needs them.
const LazyWorkspace = lazy(() => import('@/features/workspace/Workspace').then((m) => ({ default: m.WorkspacePage })))
const WorkspacePage = () => <Suspense fallback={<div className="p-5"><SkeletonRows rows={10} /></div>}><LazyWorkspace /></Suspense>

const me = (qc: QueryClient) => qc.ensureQueryData({ queryKey: qk.me(), queryFn: fetchMe })

function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter()
  return <ErrorState error={error} retry={() => { reset(); void router.invalidate() }} />
}

function NotFound() {
  return <EmptyState title="Nothing here" body="That address does not match a page. Check the link, or go home." action={<a href="/"><Button variant="primary">Go home</Button></a>} />
}

function Forbidden() {
  return <EmptyState title="You don't have access to this" body="Ask an admin if you need it. Your role decides which pages you can see." action={<a href="/"><Button>Go home</Button></a>} />
}

const root = createRootRouteWithContext<Ctx>()({
  component: Outlet,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
})

const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined)

// ── Public ──────────────────────────────────────────────────────────────────
const login = createRoute({
  getParentRoute: () => root,
  path: '/login',
  validateSearch: (s: Record<string, unknown>) => ({ next: str(s.next) }),
  beforeLoad: async ({ context }) => {
    const m = await me(context.qc)
    if (m?.stage === 'full') throw redirect({ to: '/' })
  },
  component: LoginPage,
})
const twoFactor = createRoute({ getParentRoute: () => root, path: '/login/2fa', validateSearch: (s: Record<string, unknown>) => ({ next: str(s.next) }), component: TwoFactorPage })
const enrol = createRoute({ getParentRoute: () => root, path: '/enrol', component: EnrolPage })
const invite = createRoute({ getParentRoute: () => root, path: '/invite/$token', component: InvitePage })
const forgot = createRoute({ getParentRoute: () => root, path: '/forgot', component: ForgotPage })
const reset = createRoute({ getParentRoute: () => root, path: '/reset/$token', component: ResetPage })
// Emails sent before 2026-10-27 linked `?token=`: those links still open the same pages.
const tokenSearch = (s: Record<string, unknown>) => ({ token: str(s.token) })
const inviteQ = createRoute({ getParentRoute: () => root, path: '/invite', validateSearch: tokenSearch, component: InvitePage })
const resetQ = createRoute({ getParentRoute: () => root, path: '/reset', validateSearch: tokenSearch, component: ResetPage })

// ── Signed in ───────────────────────────────────────────────────────────────
/** Where a session that is not fully signed in has to go, or null. Exported for tests. */
export function guard(m: Me | null, href: string): string | null {
  if (!m) return `/login?next=${encodeURIComponent(href)}`
  if (m.stage === 'password') return challengeFor(m) ? `/login/2fa?next=${encodeURIComponent(href)}` : '/enrol'
  return null
}

const shell = createRoute({
  getParentRoute: () => root,
  id: 'shell',
  beforeLoad: async ({ context, location }) => {
    const m = await me(context.qc)
    const to = guard(m, location.href)
    if (to) throw redirect({ href: to })
    return { me: m! }
  },
  component: Shell,
})

const gated = (cap: Capability, project?: (p: Record<string, string>) => string) =>
  ({ context, params }: { context: { me?: Me }; params: Record<string, string> }) => {
    if (!can(context.me, cap, project?.(params))) throw new ForbiddenError()
  }
class ForbiddenError extends Error {}
const forbiddenBoundary = ({ error, reset }: ErrorComponentProps) => (error instanceof ForbiddenError ? <Forbidden /> : <RouteError error={error} reset={reset} info={undefined} />)

const r = <P extends string>(path: P, component: () => React.ReactNode, extra: Record<string, unknown> = {}) =>
  createRoute({ getParentRoute: () => shell, path, component, errorComponent: forbiddenBoundary, ...extra })

const home = r('/', HomePage)
// My work and Activity are gone: old links land on the project cards.
const inbox = createRoute({ getParentRoute: () => shell, path: '/inbox', beforeLoad: () => { throw redirect({ to: '/' }) } })
const myWork = createRoute({ getParentRoute: () => shell, path: '/my-work', beforeLoad: () => { throw redirect({ to: '/' }) } })
const activity = createRoute({ getParentRoute: () => shell, path: '/activity', beforeLoad: () => { throw redirect({ to: '/' }) } })
// `ticket` opens that ticket's panel: the link on a question posted to Linear or Slack.
const projectSearch = { validateSearch: (s: Record<string, unknown>) => ({ tab: str(s.tab), ticket: str(s.ticket) }) }
const project = r('/p/$project', ProjectOverviewPage, projectSearch)
const issues = r('/p/$project/issues', IssuesPage)
const intake = r('/p/$project/intake', IntakePage)
const detailedScope = r('/p/$project/scope', DetailedScopePage)
// Errors and Releases pages are gone (error review runs on its own; features/15).
const errorsPage = createRoute({ getParentRoute: () => shell, path: '/p/$project/errors', beforeLoad: ({ params }) => { throw redirect({ href: `/p/${params.project}` }) } })
const releasesPage = createRoute({ getParentRoute: () => shell, path: '/p/$project/releases', beforeLoad: ({ params }) => { throw redirect({ href: `/p/${params.project}` }) } })
// Feedback has no page: it becomes Linear tickets; the key lives in Settings.
const feedbackPage = createRoute({ getParentRoute: () => shell, path: '/p/$project/feedback', beforeLoad: ({ params }) => { throw redirect({ href: `/p/${params.project}` }) } })
const workspace = r('/p/$project/w/$ticket', WorkspacePage)
const workspaceTab = r('/p/$project/w/$ticket/$tab', WorkspacePage)
// Sessions live in the project page's left column.
const sessions = createRoute({ getParentRoute: () => shell, path: '/p/$project/s', beforeLoad: ({ params }) => { throw redirect({ href: `/p/${params.project}` }) } })
const session = r('/p/$project/s/$session', ProjectOverviewPage, projectSearch)
const archive = r('/p/$project/archive', ArchivePage)
// CI (features/21); the Worker answers 404 for a project that keeps GitHub Actions.
const ciRuns = r('/p/$project/ci', CiRunsPage)
const ciRun = r('/p/$project/ci/$runId', CiRunPage)
const settingsIndex = createRoute({ getParentRoute: () => shell, path: '/p/$project/settings', beforeLoad: ({ params }) => { throw redirect({ href: `/p/${params.project}/settings/general` }) } })
const settings = r('/p/$project/settings/$section', ProjectOverviewPage)
const servers = r('/servers', ServersPage, { beforeLoad: gated('servers.manage') })
const server = r('/servers/$server', ServerPage, { beforeLoad: gated('servers.manage') })
const usage = r('/settings/usage', withSettingsTabs(UsagePage), { validateSearch: (s: Record<string, unknown>) => ({ range: str(s.range), project: str(s.project) }) })
const alerts = r('/alerts', AlertsPage)
const people = r('/settings/people', withSettingsTabs(PeoplePage), { beforeLoad: gated('people.invite') })
const scopesIndex = r('/scopes', ScopesPage, { beforeLoad: gated('scopes.manage') })
const scope = r('/scopes/$scope', ScopesPage, { beforeLoad: gated('scopes.manage') })
const account = r('/account', AccountPage)
// Usage, Claude accounts, Connections, Backups and People are tabs of one Settings page.
const settingsHome = createRoute({ getParentRoute: () => shell, path: '/settings', beforeLoad: () => { throw redirect({ href: '/settings/usage' }) } })
const oldUsage = createRoute({ getParentRoute: () => shell, path: '/usage', beforeLoad: () => { throw redirect({ href: '/settings/usage' }) } })
const oldPeople = createRoute({ getParentRoute: () => shell, path: '/people', beforeLoad: () => { throw redirect({ href: '/settings/people' }) } })
const connections = r('/settings/connections', withSettingsTabs(ConnectionsPage), { beforeLoad: gated('integrations.manage') })
const claudeAccounts = r('/settings/claude-accounts', withSettingsTabs(ClaudeAccountsPage))
const backups = r('/settings/backups', withSettingsTabs(BackupsPage), { beforeLoad: gated('backups.restore') })

const tree = root.addChildren([
  login, twoFactor, enrol, invite, inviteQ, forgot, reset, resetQ,
  shell.addChildren([home, inbox, myWork, activity, project, issues, intake, detailedScope, errorsPage, releasesPage, feedbackPage, workspace, workspaceTab, sessions, session, archive, ciRuns, ciRun, settingsIndex, settings, servers, server, usage, alerts, people, scopesIndex, scope, account, claudeAccounts, connections, backups, settingsHome, oldUsage, oldPeople]),
])

export function makeRouter(qc: QueryClient) {
  return createRouter({ routeTree: tree, context: { qc }, defaultPreload: 'intent', defaultPreloadStaleTime: 0 })
}
